import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
// Direct path, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { RecipientOptionType } from '../composeMode';


interface Props {
  /* Which row these recipients belong to, so a stale report cannot be read onto the next row. */
  rowKey: string;
  /*
    What the fields were seeded with. Anything else in them is the sender's doing, which is the only
    thing worth overriding the committed state with.
  */
  seededTo: RecipientOptionType[];
  seededCC: RecipientOptionType[];
  onChange: (
    open: { rowKey: string; to: RecipientOptionType[]; cc: RecipientOptionType[] } | null
  ) => void;
}

const idsOf = (list: RecipientOptionType[]) => list.map((option) => option?.id ?? '').join('|');

/*
  Reports the open row's recipients up to the dialog, once the sender has changed them.

  The row pane's fields live in the dialog's own form, but the dialog renders the provider rather
  than sitting inside it, so it cannot watch them. This does, and hands them back - which is what
  lets the list badge follow the addresses on screen instead of the ones last committed. Committing
  only happens when a row is left, so without this a row given a phone number still read Email
  until the sender clicked away.

  CC is reported alongside To because a text's recipient cap counts both.

  Reporting only a DIFFERENCE from the seed is what keeps a row's first open quiet: opening a row
  fetches its addresses, and until they arrive the field is empty - which, from the array alone,
  cannot be told apart from the sender having cleared it. Reporting that emptiness flashed
  "No recipients" over every row while its fetch was in flight. Dirty state cannot stand in for
  this: clearing the field returns it to its default, which RHF scores as clean again - exactly the
  edit that matters most here.

  Renders nothing: it exists to be inside the provider.
*/
export default function BatchOpenRowWatcher({
  rowKey,
  seededTo,
  seededCC,
  onChange,
}: Props) {
  const { control } = useFormContext();
  const to: RecipientOptionType[] = useWatch({ control, name: 'to' }) ?? [];
  const cc: RecipientOptionType[] = useWatch({ control, name: 'cc' }) ?? [];

  // useWatch hands back fresh arrays each render, so the ids are what actually changed.
  const liveKey = `${idsOf(to)}#${idsOf(cc)}`;
  const edited = liveKey !== `${idsOf(seededTo)}#${idsOf(seededCC)}`;

  useEffect(() => {
    onChange(edited ? { rowKey, to, cc } : null);
    // `to`/`cc` are deliberately absent: liveKey is their stable form, and depending on the arrays
    // themselves would re-run this on every render of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey, liveKey, edited, onChange]);

  return null;
}
